// Client-side geolocation verification using a Point + Radius Geofence model.
//
// We compare the device's live GPS reading against the property's rooftop
// coordinates. The device is considered "on-site" if the GPS reading is precise
// enough (accuracy) AND close enough (distance) to the property.
// Distance is computed using the Haversine formula (great-circle distance) directly,
// avoiding the need for heavy external GIS libraries since this is a simple point-to-point check.

/**
 * Default on-site verification radius (meters).
 * If the property does not specify a custom geofence radius, this fallback is used.
 */
export const DEFAULT_GEOFENCE_RADIUS_METERS = 150;

/**
 * Readings less precise than this threshold (accuracy radius in meters) are rejected
 * as they cannot reliably confirm the user's location within the geofence.
 */
export const MAXIMUM_ACCEPTABLE_GPS_ACCURACY_METERS = 100;

/**
 * Configuration options for getCurrentPosition/watchPosition.
 * We force high accuracy (usually requires hardware GPS chip, not just IP/Wi-Fi heuristics).
 */
export const GEO_OPTIONS: PositionOptions = {
	enableHighAccuracy: true,
	timeout: 10000,
	maximumAge: 0 // Do not use cached positions, we need real-time data
};

export type LocationVerificationReason = "on_site" | "low_accuracy" | "out_of_range";

/** Resulting state of the capture-time location verification algorithm. */
export type LocationVerificationResultState =
	| "verified"
	| "low_accuracy"
	| "out_of_range"
	| "permission_denied"
	| "unavailable";

export interface GpsReading {
	latitude: number;
	longitude: number;
	accuracy: number;
}

export interface PropertyGeofence {
	latitude?: number | null;
	longitude?: number | null;
	geofenceRadiusMeters?: number | null;
}

/**
 * The polymorphic location proof persisted to the database (evidence_submissions.location_verification).
 * Currently uses method "GPS". Extensible for future methods like NFC tags, BLE beacons, or VPS (Visual Positioning System).
 */
export interface LocationVerification {
	method: "GPS";
	verified: boolean;
	reason: LocationVerificationReason;
	accuracy_meters: number | null;
	distance_to_property_meters: number | null;
	geofence_radius_meters: number;
	coordinates: { lat: number; lng: number } | null;
	captured_at: string;
	exception_acknowledged_at?: string | null;
	exception_acknowledged_by?: string | null;
}

const EARTH_RADIUS_METERS = 6371000;
const convertDegreesToRadians = (degrees: number): number => (degrees * Math.PI) / 180;

/**
 * Computes the great-circle distance in meters between two WGS84 coordinates using the Haversine formula.
 * This accounts for the spherical shape of the Earth, which is accurate enough for short distances (like geofencing).
 */
export const calculateHaversineDistanceMeters = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
	const deltaLatRadians = convertDegreesToRadians(lat2 - lat1);
	const deltaLngRadians = convertDegreesToRadians(lng2 - lng1);
	const haversineA =
		Math.sin(deltaLatRadians / 2) ** 2 +
		Math.cos(convertDegreesToRadians(lat1)) *
			Math.cos(convertDegreesToRadians(lat2)) *
			Math.sin(deltaLngRadians / 2) ** 2;
	return 2 * EARTH_RADIUS_METERS * Math.asin(Math.min(1, Math.sqrt(haversineA)));
};

export interface LocationEvaluation {
	state: Exclude<LocationVerificationResultState, "permission_denied" | "unavailable">;
	reason: LocationVerificationReason;
	distanceMeters: number | null;
	radiusMeters: number;
}

/**
 * Evaluates a raw GPS reading against a property's target coordinates.
 * Flow:
 *  1. If device GPS accuracy is worse than our acceptable threshold -> 'low_accuracy' (distance computation is meaningless).
 *  2. If the property lacks target coordinates -> 'low_accuracy' (fails closed, routing to exception flow).
 *  3. Calculate Haversine distance.
 *  4. If distance > geofence radius -> 'out_of_range'
 *  5. Otherwise -> 'verified'
 */
export const evaluateGpsLocationAgainstGeofence = (
	reading: GpsReading,
	property: PropertyGeofence
): LocationEvaluation => {
	console.log("[GPS Testing] Evaluating location...", { reading, property });
	const radiusMeters = property.geofenceRadiusMeters ?? DEFAULT_GEOFENCE_RADIUS_METERS;

	if (reading.accuracy > MAXIMUM_ACCEPTABLE_GPS_ACCURACY_METERS) {
		return { state: "low_accuracy", reason: "low_accuracy", distanceMeters: null, radiusMeters };
	}

	if (property.latitude == null || property.longitude == null) {
		// No target coordinates to compare against — cannot safely confirm on-site. Route to exception fallback.
		return { state: "low_accuracy", reason: "low_accuracy", distanceMeters: null, radiusMeters };
	}

	const distanceMeters = Math.round(
		calculateHaversineDistanceMeters(reading.latitude, reading.longitude, property.latitude, property.longitude)
	);
	console.log("[GPS Testing] Computed distance to property (m):", distanceMeters, "Radius allowed:", radiusMeters);

	if (distanceMeters > radiusMeters) {
		return { state: "out_of_range", reason: "out_of_range", distanceMeters, radiusMeters };
	}

	return { state: "verified", reason: "on_site", distanceMeters, radiusMeters };
};

/** Build the location_verification payload for a submission. */
export const buildLocationVerification = (args: {
	reading: GpsReading | null;
	evaluation: LocationEvaluation;
	acknowledgedBy?: string | null;
}): LocationVerification => {
	const { reading, evaluation, acknowledgedBy } = args;
	const verified = evaluation.state === "verified";
	return {
		method: "GPS",
		verified,
		reason: evaluation.reason,
		accuracy_meters: reading ? Math.round(reading.accuracy) : null,
		distance_to_property_meters: evaluation.distanceMeters,
		geofence_radius_meters: evaluation.radiusMeters,
		coordinates: reading ? { lat: reading.latitude, lng: reading.longitude } : null,
		captured_at: new Date().toISOString(),
		// Only set on the exception path (unverified capture the user acknowledged).
		...(verified
			? {}
			: {
					exception_acknowledged_at: new Date().toISOString(),
					exception_acknowledged_by: acknowledgedBy ?? null
				})
	};
};
